<template>
  <div :class="$style.container" :style="containerStyle">
    <div
      :class="$style.scrim"
      :style="scrimStyle"
      data-testid="nsDrawerScrim"
      @click="$emit('close')"
    />
    <div
      ref="content"
      :class="$style.content"
      :style="contentStyle"
      @touchmove="handleTouchMove"
      @touchend="handleTouchEnd"
    >
      <slot />
    </div>
  </div>
</template>

<script>
import { convertToUnit } from "../../utils/helpers";

/**
 * Ns Drawer
 * Slides open from left. Clicking the background will fire close event.
 *
 * @event close Fires when the drawer requests to close
 */

const ANIMATION_LENGTH = 200;
const SWIPE_CLOSE_PERCENTAGE = 0.3333;

export default {
  name: "NsMinimalDrawer",
  props: {
    /**
     * Whether the content is visible or not
     */
    open: Boolean,
    /**
     * Width of drawer
     */
    width: {
      type: [Number, String],
      default: 256
    }
  },
  data() {
    return {
      // container needs to stay visible longer than open prop, for animation to
      // complete.
      isVisible: this.open,
      swipePercentage: 0,
      touchStartPosition: null,
      timeoutId: null
    };
  },
  computed: {
    containerStyle() {
      return { visibility: this.isVisible ? "visible" : "hidden" };
    },
    scrimStyle() {
      if (this.swipePercentage > 0) {
        return {
          opacity: 1 - this.swipePercentage,
          transitionDuration: "0ms"
        };
      } else {
        return {
          opacity: this.open ? 1 : 0,
          transitionDuration: `${ANIMATION_LENGTH}ms`
        };
      }
    },
    contentStyle() {
      if (this.swipePercentage > 0) {
        return {
          width: convertToUnit(this.width),
          transitionDuration: "0ms",
          transform: `translate3d(-${this.swipePercentage * 100}%, 0, 0)`
        };
      } else {
        return {
          width: convertToUnit(this.width),
          transitionDuration: `${ANIMATION_LENGTH}ms`,
          transform: this.open ? "translate3d(0, 0, 0)" : ""
        };
      }
    }
  },
  watch: {
    open(open) {
      this.animate(open);
    }
  },
  destroyed() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
  },
  methods: {
    animate(open) {
      if (this.timeoutId) {
        clearTimeout(this.timeoutId);
      }

      if (open) {
        this.isVisible = open;
      } else {
        this.timeoutId = setTimeout(() => {
          this.isVisible = open;
        }, ANIMATION_LENGTH);
      }
    },
    handleTouchMove(event) {
      const contentWidth = this.$refs.content.clientWidth;
      const touch = event.targetTouches[0];
      const touchPosition = touch.pageX;

      if (this.touchStartPosition == null) {
        this.touchStartPosition = touch.pageX;
      }

      const touchMovement = this.touchStartPosition - touchPosition;
      this.swipePercentage = touchMovement / contentWidth;
    },
    handleTouchEnd() {
      if (this.swipePercentage > SWIPE_CLOSE_PERCENTAGE) {
        this.$emit("close");
      }

      this.swipePercentage = 0;
      this.touchStartPosition = null;
    }
  }
};
</script>

<style lang="scss" module>
.container {
  position: fixed;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
  visibility: hidden;
  transition-property: visibility;
  touch-action: pan-y;
  z-index: 900;
}

.scrim {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  transition-property: opacity;
  transform: translateZ(0);
  opacity: 0;
  background: rgba(0, 0, 0, 0.5);
}

.content {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 256px;
  transition-property: transform;
  transform: translate3d(-100%, 0, 0);
  background-color: #fff;
}
</style>
